import { useCallback, useEffect, useMemo, useState } from 'react';
import api, { ApiError } from '../../lib/apiClient';
import { IconButton } from '../IconButton';
import { IconDownload } from '../icons';
import Pagination from '../Pagination';
import { useConfig, labelFor } from '../../context/ConfigContext';
import { downloadFile } from '../../utils/download';
import { formatMoney } from '../../utils/format';

// TS port of the source (Falah) app's components/reports/ReportView.jsx —
// the shared shell every report page in this app wraps: filter bar (only
// the filters the report declares it supports), search, a sortable/
// paginated table, optional summary KPI cards, and an Export PDF button
// (plus an Export Excel button, on the reports whose backend controller
// implements the matching `{endpoint}/xlsx` route — see the `xlsx` prop)
// that reuse the same filters.
//
// One deliberate difference from the source:
//  - No programme/agent/vendor/volunteer filters. Those all belonged to
//    the source app's Programmes/Volunteers modules, which this port does
//    not carry over — every kept report (Memberships, Revenue, Procurement,
//    Resources, Announcements, Events) only ever uses 'status', 'category',
//    and 'dateRange' (verified against every *ReportController on the
//    backend — see routes/api.php and app/Http/Controllers/
//    Concerns/BuildsReports.php).
export interface ReportColumn {
  key: string;
  label: string;
  align?: 'right';
  sortable?: boolean;
  type?: 'money' | 'percent';
}

export type ReportFilterKind = 'status' | 'category' | 'dateRange' | 'account';

export interface ReportOption {
  value: string;
  label: string;
}

interface ReportSummaryItem {
  label: string;
  value: number;
  type: 'money' | 'percent' | 'count';
}

export type ReportRow = Record<string, string | number | null>;

interface ReportPage {
  data: ReportRow[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  summary: ReportSummaryItem[] | null;
}

interface ReportViewProps {
  title: string;
  description?: string;
  endpoint: string;
  columns: ReportColumn[];
  filters?: ReportFilterKind[];
  statusOptions?: ReportOption[];
  categoryOptions?: ReportOption[];
  accountOptions?: ReportOption[];
  /** Pre-selects the 'account' filter — for a deep link like a Trial
   * Balance drill-down (?chart_of_account_id=...). */
  initialAccountId?: string;
  /** column key -> list_key, for cells that should render via labelFor()
   * instead of the raw value (mirrors the source's `listColumns` prop). */
  listColumns?: Record<string, string>;
  defaultSort?: string;
  /** Shows an Export Excel button alongside Export PDF. Only set this on a
   * report whose backend controller has a matching `{endpoint}/xlsx` route. */
  xlsx?: boolean;
}

export default function ReportView({
  title,
  description,
  endpoint,
  columns,
  filters = [],
  statusOptions = [],
  categoryOptions = [],
  accountOptions = [],
  initialAccountId,
  listColumns = {},
  defaultSort,
  xlsx = false,
}: ReportViewProps) {
  const { settings, lists } = useConfig();
  const fmtMoney = (n: number) => formatMoney(n, settings.currency);

  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');
  const [accountId, setAccountId] = useState(initialAccountId ?? '');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState(defaultSort ?? '');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const [data, setData] = useState<ReportPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportingXlsx, setExportingXlsx] = useState(false);

  const filterParams = useMemo(
    () => ({
      status: status || undefined,
      category: category || undefined,
      chart_of_account_id: accountId || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      search: search || undefined,
      sort_by: sortBy || undefined,
      sort_dir: sortDir,
    }),
    [status, category, accountId, dateFrom, dateTo, search, sortBy, sortDir]
  );

  const params = useMemo(
    () => ({ ...filterParams, page, per_page: 20 }),
    [filterParams, page]
  );

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    api
      .get<ReportPage>(endpoint, { params })
      .then((res) => setData(res.data))
      .catch((err: ApiError) => setError(err.message || 'Failed to load report.'))
      .finally(() => setLoading(false));
  }, [endpoint, params]);

  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  const resetPage = <T,>(setter: (value: T) => void) => (value: T) => {
    setter(value);
    setPage(1);
  };

  const toggleSort = (key: string) => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
    setPage(1);
  };

  const exportPdf = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      await downloadFile(api, `${endpoint}/pdf`, `${title.toLowerCase().replace(/\s+/g, '-')}.pdf`, filterParams);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Export failed.');
    } finally {
      setExporting(false);
    }
  };

  const exportXlsx = async () => {
    if (exportingXlsx) return;
    setExportingXlsx(true);
    try {
      await downloadFile(api, `${endpoint}/xlsx`, `${title.toLowerCase().replace(/\s+/g, '-')}.xlsx`, filterParams);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Export failed.');
    } finally {
      setExportingXlsx(false);
    }
  };

  const renderCell = (col: ReportColumn, row: ReportRow) => {
    const value = row[col.key];
    if (value === null || value === undefined || value === '') return <span className="muted">—</span>;
    if (col.type === 'money') return fmtMoney(Number(value));
    if (col.type === 'percent') return `${value}%`;
    if (listColumns[col.key]) return labelFor(lists, listColumns[col.key], String(value));
    return value;
  };

  return (
    <>
      <header className="page-header">
        <h1>{title}</h1>
      </header>
      {description && <p className="muted">{description}</p>}

      <div className="toolbar">
        <input
          type="search"
          placeholder="Search…"
          value={search}
          onChange={(e) => resetPage(setSearch)(e.target.value)}
        />
        {filters.includes('status') && (
          <select value={status} onChange={(e) => resetPage(setStatus)(e.target.value)}>
            <option value="">All statuses</option>
            {statusOptions.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        )}
        {filters.includes('category') && (
          <select value={category} onChange={(e) => resetPage(setCategory)(e.target.value)}>
            <option value="">All categories</option>
            {categoryOptions.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        )}
        {filters.includes('account') && (
          <select value={accountId} onChange={(e) => resetPage(setAccountId)(e.target.value)}>
            <option value="">Select an account…</option>
            {accountOptions.map((a) => (
              <option key={a.value} value={a.value}>
                {a.label}
              </option>
            ))}
          </select>
        )}
        {filters.includes('dateRange') && (
          <>
            <label className="checkbox-inline">
              From
              <input type="date" value={dateFrom} onChange={(e) => resetPage(setDateFrom)(e.target.value)} />
            </label>
            <label className="checkbox-inline">
              To
              <input type="date" value={dateTo} onChange={(e) => resetPage(setDateTo)(e.target.value)} />
            </label>
          </>
        )}
        <IconButton
          icon={IconDownload}
          label={exporting ? 'Exporting…' : 'Export PDF'}
          onClick={exportPdf}
          disabled={exporting}
        />
        {xlsx && (
          <IconButton
            icon={IconDownload}
            label={exportingXlsx ? 'Exporting…' : 'Export Excel'}
            onClick={exportXlsx}
            disabled={exportingXlsx}
          />
        )}
      </div>

      {data?.summary && (
        <div className="stat-grid">
          {data.summary.map((s) => (
            <div className="card stat" key={s.label}>
              <span className="stat-label">{s.label}</span>
              <span className="stat-value">
                {s.type === 'money' ? fmtMoney(s.value) : s.type === 'percent' ? `${s.value}%` : s.value}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <div className="report-table-scroll">
          <table>
            <thead>
              <tr>
                {columns.map((col) => {
                  const sortable = col.sortable !== false;
                  const isSorted = sortable && sortBy === col.key;
                  return (
                    <th
                      key={col.key}
                      className={`${col.align === 'right' ? 'right' : ''} ${sortable ? 'sortable' : ''}`.trim()}
                      onClick={sortable ? () => toggleSort(col.key) : undefined}
                      role={sortable ? 'button' : undefined}
                      tabIndex={sortable ? 0 : undefined}
                      onKeyDown={
                        sortable
                          ? (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                toggleSort(col.key);
                              }
                            }
                          : undefined
                      }
                      aria-sort={isSorted ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                    >
                      {col.label}
                      {isSorted && <span className="sort-arrow">{sortDir === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={columns.length} className="muted">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={columns.length} className="error">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.map((row, i) => (
                  <tr key={(row.id as string | number | undefined) ?? i}>
                    {columns.map((col) => (
                      <td key={col.key} className={col.align === 'right' ? 'right' : ''}>
                        {renderCell(col, row)}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length} className="muted">
                    No records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination meta={data} onPage={setPage} />
      </div>
    </>
  );
}
